/**
 * HUD layout — the bottom HUD STRIP the arena viewport is carved out of (#781).
 *
 * PURE. No Pixi, no DOM, no React, no time. This is the render layer's own
 * description of the screen-anchored HUD's footprint, and it is the CANONICAL
 * home of {@link HUD_SCALE} — `ui/hudScale.tsx` re-exports it. That direction is
 * deliberate: `render/` must never import from `ui/` (the camera would then
 * depend on React chrome), yet the camera has to know how tall the bottom HUD
 * is. Owning the number here and re-exporting upward keeps ONE source of truth
 * without adding a `render/ → ui/` edge.
 *
 * ── Why a reserved strip at all (#679 → #730 → #781) ─────────────────────────
 * `ActionBar` is a fixed DOM overlay drawn ON TOP of the canvas. #679 first
 * modelled its footprint so the follow camera would not clamp a champion
 * underneath the ability icons. #730 then made "zero black, ever" a hard
 * property and, because feeding the reserve into the edge CLAMP stops the map
 * short of the canvas bottom, demoted the reserve to a bias on the CENTERING
 * TARGET only. That is why a champion at the map's south edge slid back under
 * the action bar: the clamp still used the full canvas height, so the camera
 * happily put the map's bottom edge — and the champion standing on it — behind
 * the HUD.
 *
 * The two goals are contradictory only while "zero black" is defined over the
 * RAW CANVAS. #781 redefines it over the ARENA VIEWPORT:
 *
 *     reserve = max(HUD_RESERVE_MIN_PX, round(FRACTION · canvasH))
 *     arenaH  = canvasH − reserve
 *
 * and feeds `arenaH` to BOTH the cover zoom and the `cy` clamp (`camera.ts`).
 * Every pixel of `canvasW × arenaH` shows playable area from every camera
 * position, corners included; the strip below it is INTERFACE, not "non-playable
 * area leaking in". It is black today purely as a placeholder — later it holds
 * League-style HUD and/or south-edge wall art, neither of which changes any
 * camera math.
 *
 * ── Why the reserve SCALES ────────────────────────────────────────────────────
 * The old flat `HUD_RESERVE_BOTTOM_PX = 100` modelled the action bar as
 * `bottom: 32` + a 56px slot + headroom — correct until #755 shipped
 * `HUD_SCALE = 1.2`, which makes the real footprint `(32 + 56) · 1.2 = 105.6px`.
 * The reserve has been ~6px short ever since (a known-stale follow-up flagged in
 * `ui/hudScale.tsx`'s header). A viewport-proportional reserve with a FLOOR
 * above the true footprint removes that whole class of bug: the floor can never
 * be under the HUD's own size, and the fraction keeps the strip proportionate on
 * large displays instead of a fixed sliver.
 *
 * Reserving height only costs zoom when the HEIGHT constraint binds the cover
 * zoom, so on a wide map (`bare`, 4000×4000) it is free at every tested
 * resolution; the worst measured case is a champion ~9–15% smaller on
 * `landscape`.
 *
 * PRESENTATION ONLY — floats are legal here. Nothing crosses the wire, no sim
 * state, no hash: no `SIM_VERSION` bump, no golden regen, no `WIRE_EPOCH`.
 */

/**
 * Uniform multiplier applied to the screen-anchored in-match HUD (#755). `1` is
 * "native size"; 1.2 (+20%) per Joseph's request so icons/frames/bars read
 * bigger at the same relative layout.
 *
 * THE one knob for HUD size — change it here, nowhere else. It lives in
 * `render/` rather than beside the wrappers that apply it (`ui/hudScale.tsx`,
 * which re-exports it) because {@link HUD_BOTTOM_FOOTPRINT_PX} — and through it
 * the camera's arena viewport — is derived from it, and `render/` cannot import
 * from `ui/`.
 */
export const HUD_SCALE = 1.2;

/** `ActionBar`'s CSS `bottom` offset, in UNSCALED layout px (see
 *  `ui/ActionBar.tsx`). Multiplied by {@link HUD_SCALE} on screen. */
export const ACTION_BAR_BOTTOM_INSET_PX = 32;

/** Height of one `ActionBar` slot, in UNSCALED layout px (slots are 56×56 —
 *  see `ui/ActionBar.tsx`). Multiplied by {@link HUD_SCALE} on screen. */
export const ACTION_BAR_SLOT_PX = 56;

/**
 * True on-screen height of the bottom HUD's footprint, in CSS px: the action
 * bar's bottom inset plus one slot row, scaled by {@link HUD_SCALE}
 * (`(32 + 56) · 1.2 = 105.6`). Derived, never hand-tuned — this is the number
 * {@link HUD_RESERVE_MIN_PX} must stay above, and a UI test pins the two
 * layout literals against `ActionBar`'s rendered DOM so the derivation cannot
 * silently go stale the way the flat `100` did.
 */
export const HUD_BOTTOM_FOOTPRINT_PX =
  (ACTION_BAR_BOTTOM_INSET_PX + ACTION_BAR_SLOT_PX) * HUD_SCALE;

/**
 * Floor for the reserved strip, in CSS px. Above
 * {@link HUD_BOTTOM_FOOTPRINT_PX} (105.6) with a few px of headroom, so the
 * action bar can never overlap the arena viewport no matter how short the
 * window is. Raising `HUD_SCALE` past ~1.25 makes the footprint exceed this
 * floor — the regression test in `hudLayout.test.ts` fails loudly rather than
 * letting the shortfall ship again.
 */
export const HUD_RESERVE_MIN_PX = 110;

/** Fraction of canvas height the strip grows to once that exceeds the floor
 *  (9% — 130px at 1440p, 194px at 2160p, floor-bound at and below 1080p). */
export const HUD_RESERVE_VIEWPORT_FRACTION = 0.09;

/**
 * Hard cap on the strip as a fraction of canvas height, for degenerate/tiny
 * viewports only ({@link HUD_RESERVE_MIN_PX} exceeds half the canvas below
 * ~220px tall). Guarantees `arenaH > 0`, so the cover zoom can never come out
 * `0`/`Infinity`/`NaN`. No shipped viewport reaches it.
 */
const MAX_RESERVE_FRACTION_OF_CANVAS = 0.5;

/**
 * Height (CSS px) to reserve at the bottom of a `canvasH`-tall canvas for the
 * bottom HUD strip: `max(floor, round(fraction · canvasH))`.
 *
 * Rounded (not floored/ceiled) so the strip is symmetric about the ideal
 * fraction; the exact rounding is cosmetic — nothing hashed depends on it.
 * A non-positive or non-finite canvas reserves nothing (there is no viewport to
 * carve up).
 */
export function hudReserveBottomPx(canvasH: number): number {
  if (!Number.isFinite(canvasH) || canvasH <= 0) return 0;
  const scaled = Math.round(HUD_RESERVE_VIEWPORT_FRACTION * canvasH);
  return Math.max(HUD_RESERVE_MIN_PX, scaled);
}

/**
 * Height (CSS px) of the ARENA VIEWPORT: the canvas minus the reserved bottom
 * strip. This is the rectangle #781's zero-black invariant is defined over, and
 * the height `camera.ts` feeds to BOTH the cover zoom and the `cy` clamp.
 *
 * Defensive by design — it is the ONE place that guarantees a usable arena
 * height, since the reserve reaches `computeCameraCenter` as a caller-supplied
 * number: a negative/non-finite reserve counts as none, and an over-large one is
 * capped at {@link MAX_RESERVE_FRACTION_OF_CANVAS} of the canvas so the result
 * is always `> 0` for a positive canvas (never `0`, `Infinity` or `NaN` zoom
 * downstream). A non-positive canvas yields `0`, which the cover zoom already
 * treats as the degenerate "static centering" case.
 */
export function arenaViewportHeight(canvasH: number, reserveBottomPx: number): number {
  if (!Number.isFinite(canvasH) || canvasH <= 0) return 0;
  const reserve = Number.isFinite(reserveBottomPx) && reserveBottomPx > 0 ? reserveBottomPx : 0;
  return Math.max(canvasH * (1 - MAX_RESERVE_FRACTION_OF_CANVAS), canvasH - reserve);
}
